import { useEffect, useMemo, useState } from 'react';
import PosPlanCard from './PosPlanCard';
import PosSubscribeModal from './PosSubscribeModal';
import PosSubscriptionStatusPill from './PosSubscriptionStatusPill';
import { useConfig } from '../context/ConfigContext';
import { useClientSubscription } from '../hooks/useClientSubscription';
import { spinnerRing } from '../utils/bookingUtils';
import { formatMoney } from '../utils/currencyUtil';
import {
  formatPlanDate,
  formatPlanDateInZone,
  formatPlanDuration,
  formatTimeZoneLabel,
  getSubscriptionState,
  resolveTimeZone,
} from '../utils/subscriptionDateUtil';
import { subscribeSubscriptionPlans, clientSelectablePlans } from '../utils/posSubscriptionUtil';
import {
  POS_APPLICATION_STATUS,
  POS_APPLICATION_STATUS_LABELS,
  POS_SUBSCRIPTION_STATUS,
  POS_SUBSCRIPTION_REVIEW_WINDOW,
} from '../constants/posConst';
import '../css/Pos.css';
import '../css/PosSubscription.css';

/**
 * Subscriptions — this club's current plan, the other plans it could move
 * to, and what it has applied for.
 *
 * THERE IS NO CANCEL. A plan simply runs to its end date and is renewed by
 * hand, so the only action here is to renew or switch — the same Subscribe
 * dialog the banner opens, optionally with a plan already chosen.
 *
 * Gated behind manageConfig in POS_TAB_PERMISSIONS: the people who may pay
 * are the people who see this screen.
 *
 * @returns {JSX.Element}
 */
function PosSubscriptionScreen() {
  const { config, client, clientId } = useConfig();
  const { subscription, applications, error } = useClientSubscription(clientId);
  const [plans, setPlans] = useState(null);
  const [plansError, setPlansError] = useState(null);
  // undefined = dialog closed; '' = open on the plan list; an id = open with
  // that plan already chosen.
  const [subscribing, setSubscribing] = useState(undefined);

  useEffect(() => subscribeSubscriptionPlans(
    (list) => { setPlans(clientSelectablePlans(list)); setPlansError(null); },
    (loadError) => { setPlans([]); setPlansError(loadError); },
  ), []);

  const timeZone = resolveTimeZone(client?.timezone);
  const state = useMemo(() => getSubscriptionState(subscription || null, timeZone), [subscription, timeZone]);

  if ((subscription === undefined && !error) || plans === null) {
    return <div className="pos-loading">{spinnerRing('Loading your subscription')}</div>;
  }

  const pending = applications.find((application) => application.status === POS_APPLICATION_STATUS.PENDING);
  const hasPlan = Boolean(subscription);
  const renewLabel = !hasPlan || subscription.isTrial ? 'Subscribe' : 'Renew subscription';

  return (
    <div className="ps-client-screen">
      <section className="pos-section">
        <div className="pos-section-head pos-section-head-row">
          <div>
            <h2>Your subscription</h2>
            <p>
              All dates are in {formatTimeZoneLabel(timeZone)} ({timeZone}). Plans are renewed manually —
              nothing is charged automatically.
            </p>
          </div>
          {!pending && hasPlan && (
            <button type="button" className="pos-btn ps-btn-go" onClick={() => setSubscribing('')}>{renewLabel}</button>
          )}
        </div>

        {error ? (
          <p className="pos-empty-line">Your subscription could not be loaded right now. Please try again shortly.</p>
        ) : hasPlan ? (
          <div className="ps-mine">
            <div className="ps-mine-head">
              <span className="ps-mine-name">{subscription.planName}</span>
              <PosSubscriptionStatusPill status={state.status} daysLeft={state.daysLeft} />
            </div>
            <dl className="ps-mine-facts">
              <div><dt>Started</dt><dd>{formatPlanDateInZone(subscription.startDate, timeZone)}</dd></div>
              <div><dt>{state.status === POS_SUBSCRIPTION_STATUS.EXPIRED ? 'Ended' : 'Ends'}</dt><dd>{formatPlanDateInZone(subscription.endDate, timeZone)}</dd></div>
              <div>
                <dt>Days left</dt>
                <dd>{state.status === POS_SUBSCRIPTION_STATUS.EXPIRED ? 'None' : Math.max(0, state.daysLeft) + (state.daysLeft === 0 ? ' (last day)' : '')}</dd>
              </div>
              <div>
                <dt>Plan</dt>
                <dd>
                  {subscription.isTrial || Number(subscription.price) === 0 ? 'Free' : formatMoney(subscription.price, config)}
                  {subscription.durationValue ? ` · ${formatPlanDuration(subscription.durationValue, subscription.durationUnit)}` : ''}
                </dd>
              </div>
              {subscription.extraStaff > 0 && (
                <div><dt>Extra staff</dt><dd>{subscription.extraStaff} account{subscription.extraStaff === 1 ? '' : 's'}</dd></div>
              )}
            </dl>
          </div>
        ) : (
          <div className="ps-mine ps-mine-empty">
            <p className="pos-empty-line">You don&rsquo;t have a subscription yet. Choose a plan below to get started.</p>
          </div>
        )}

        {pending && (
          <p className="ps-mine-note">
            Your application for <strong>{pending.planName}</strong> ({formatMoney(pending.total, config)}) is under
            review — usually within {POS_SUBSCRIPTION_REVIEW_WINDOW}. You can apply again once it has been reviewed.
          </p>
        )}
      </section>

      <section className="pos-section">
        <div className="pos-section-head">
          <h2>Available plans</h2>
          <p>
            {hasPlan
              ? 'Switch to another plan when you renew. A new plan starts the day after your current one ends, so you never lose paid days.'
              : 'Pick the plan that suits your club.'}
          </p>
        </div>

        {plansError ? (
          <p className="pos-empty-line">The plans could not be loaded right now. Please try again shortly.</p>
        ) : plans.length === 0 ? (
          <p className="pos-empty-line">No plans are on offer right now. Please contact The Website Pal.</p>
        ) : (
          <div className="ps-plan-grid">
            {plans.map((plan) => (
              <PosPlanCard
                key={plan.id}
                plan={plan}
                config={config}
                isCurrent={subscription?.planId === plan.id}
                // Hidden while an application is under review, so a club
                // cannot pay twice for the same period by accident.
                onSelect={pending ? undefined : () => setSubscribing(plan.id)}
                selectLabel={subscription?.planId === plan.id ? 'Renew this plan' : 'Choose this plan'}
              />
            ))}
          </div>
        )}
      </section>

      {applications.length > 0 && (
        <section className="pos-section">
          <div className="pos-section-head">
            <h2>Recent applications</h2>
            <p>Your latest subscription payments and how they were reviewed.</p>
          </div>
          <div className="ps-history-wrap">
            <table className="ps-history">
              <thead>
                <tr><th>Submitted</th><th>Plan</th><th>Total</th><th>Status</th></tr>
              </thead>
              <tbody>
                {applications.map((application) => (
                  <tr key={application.id}>
                    <td>{new Date(application.submittedAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</td>
                    <td>
                      {application.planName}
                      {(application.extraStaffEmails || []).length > 0 && (
                        <span className="ps-muted"> + {application.extraStaffEmails.length} staff</span>
                      )}
                    </td>
                    <td>{formatMoney(application.total, config)}</td>
                    <td>
                      <span className={`ps-pill ps-app-${application.status}`}>
                        {POS_APPLICATION_STATUS_LABELS[application.status] || application.status}
                      </span>
                      {application.status === POS_APPLICATION_STATUS.REJECTED && application.rejectReason && (
                        <span className="ps-history-reason">{application.rejectReason}</span>
                      )}
                      {application.status === POS_APPLICATION_STATUS.APPROVED && application.activatedEnd && (
                        <span className="ps-history-reason">
                          {formatPlanDate(application.activatedStart)} – {formatPlanDateInZone(application.activatedEnd, timeZone)}
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      <PosSubscribeModal
        isOpen={subscribing !== undefined}
        initialPlanId={subscribing || ''}
        onClose={() => setSubscribing(undefined)}
      />
    </div>
  );
}

export default PosSubscriptionScreen;
